import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api, formatPrice } from "@/lib/api";
import { Check, Shield } from "lucide-react";
import { toast } from "sonner";

export default function PaymentReturn() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const depositSessionId = params.get("deposit_session_id");
  const bookingId = params.get("booking_id");
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState(depositSessionId ? "deposit_done" : "checking");
  const [booking, setBooking] = useState(null);
  const [depositBusy, setDepositBusy] = useState(false);
  const polled = useRef(0);

  // Option B flow: when returning from deposit auth, automatically launch main payment
  useEffect(() => {
    if (!depositSessionId || !bookingId) return;
    let cancelled = false;
    const chain = async () => {
      try {
        // Confirm deposit is authorized
        await api.get(`/bookings/${bookingId}/deposit/status`);
        const r = await api.get(`/bookings/${bookingId}`);
        if (cancelled) return;
        setBooking(r.data.booking);
        // If main payment NOT yet done → trigger it now
        if (r.data.booking?.payment_status !== "paid") {
          const next = await api.post(`/bookings/${bookingId}/continue-to-payment`, {
            origin_url: window.location.origin,
          });
          window.location.href = next.data.url;
          return;
        }
        setStatus("paid");
      } catch (err) {
        if (process.env.NODE_ENV !== "production") {
          // eslint-disable-next-line no-console
          console.warn("post-deposit chain failed", err);
        }
        if (!cancelled) setStatus("error");
      }
    };
    chain();
    return () => { cancelled = true; };
  }, [depositSessionId, bookingId]);

  // Poll main payment status
  useEffect(() => {
    if (depositSessionId) return;  // skip — already paid
    if (!sessionId) { navigate("/"); return; }
    let cancelled = false;
    const poll = async () => {
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setBooking(r.data.booking);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        polled.current += 1;
        if (polled.current >= 8) { setStatus("timeout"); return; }
        setTimeout(poll, 2000);
      } catch {
        if (!cancelled) setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, depositSessionId, navigate]);

  const startDepositHold = async () => {
    setDepositBusy(true);
    try {
      const r = await api.post(`/bookings/${booking.booking_id}/deposit/checkout`, {
        origin_url: window.location.origin,
      });
      if (r.data.skipped) {
        toast.success("No deposit required");
        setDepositBusy(false);
        return;
      }
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start deposit hold");
      setDepositBusy(false);
    }
  };

  if (status === "checking" || status === "chaining") {
    return <div className="py-32 text-center text-[#8C7A6B]" data-testid="payment-checking">{status === "chaining" ? "Caution authorized — redirecting to payment…" : "Confirming your payment…"}</div>;
  }
  if (status !== "paid" && status !== "deposit_done") {
    return (
      <div className="py-32 text-center" data-testid="payment-failed">
        <p className="text-[#8C7A6B]">We could not confirm your payment.</p>
        <Link to={`/checkout/${bookingId}`} className="btn-secondary mt-6 inline-block">Try again</Link>
      </div>
    );
  }

  const lang = i18n.language;
  const depositDone = booking?.deposit_status === "authorized" || booking?.deposit_status === "captured";
  const needsDeposit = booking?.security_deposit > 0 && !depositDone;

  return (
    <div data-testid="confirmation-page" className="max-w-3xl mx-auto px-6 py-20 text-center">
      <div className="h-16 w-16 rounded-full bg-[#C86D53] text-white inline-flex items-center justify-center mb-6">
        <Check className="h-8 w-8" />
      </div>
      <p className="overline mb-3">{t("confirmation.title")}</p>
      <h1 className="font-serif text-5xl text-[#2C2A29]">{t("confirmation.title")}</h1>
      <p className="text-[#595450] mt-3">{t("confirmation.sub")}</p>

      {booking && (
        <div className="mt-10 bg-white rounded-2xl p-8 border border-[#EAE3D9] text-start" data-testid="confirmation-summary">
          <div className="flex justify-between text-sm text-[#595450] mb-2"><span>{t("detail.check_in")}</span><span>{booking.check_in}</span></div>
          <div className="flex justify-between text-sm text-[#595450] mb-2"><span>{t("detail.check_out")}</span><span>{booking.check_out}</span></div>
          <div className="flex justify-between text-sm text-[#595450] mb-2"><span>{t("detail.guests")}</span><span>{booking.guests}</span></div>
          <div className="flex justify-between font-medium text-[#2C2A29] mt-4 pt-4 border-t border-[#EAE3D9]">
            <span>{t("admin.paid")}</span><span className="font-serif text-2xl">{formatPrice(booking.total_due_now, booking.currency)}</span>
          </div>
          {booking.security_deposit > 0 && (
            <div className="flex justify-between text-xs text-[#8C7A6B] mt-2" data-testid="deposit-line">
              <span>+ {t("detail.deposit")}</span>
              <span>{formatPrice(booking.security_deposit, booking.currency)}</span>
            </div>
          )}
        </div>
      )}

      {needsDeposit && (
        <div className="mt-8 bg-[#F4EFE6] border border-[#EAE3D9] rounded-2xl p-8 text-start" data-testid="deposit-cta">
          <div className="inline-flex items-center gap-2 text-[#C86D53] mb-2">
            <Shield className="h-4 w-4" /> <span className="overline text-[#C86D53]">{t("detail.deposit")}</span>
          </div>
          <h3 className="font-serif text-2xl text-[#2C2A29]">{formatPrice(booking.security_deposit, booking.currency)}</h3>
          <p className="text-sm text-[#595450] mt-2 max-w-md leading-relaxed">
            We&rsquo;ll place a refundable hold on your card for the security deposit. The amount is never charged unless there&rsquo;s damage, and it&rsquo;s released within 14 days of check-out.
          </p>
          <button onClick={startDepositHold} disabled={depositBusy} className="btn-primary mt-5" data-testid="authorize-deposit-btn">
            {depositBusy ? "..." : `Authorize ${formatPrice(booking.security_deposit, booking.currency)} hold`}
          </button>
        </div>
      )}

      {depositDone && (
        <p className="text-sm text-[#3F5036] mt-6" data-testid="deposit-confirmed">
          ✓ Deposit hold confirmed. It will be released after check-out.
        </p>
      )}

      <Link to="/" className="btn-secondary inline-block mt-10" data-testid="back-home-btn">{t("confirmation.back_home")}</Link>
    </div>
  );
}
